import React, { useEffect, useRef, useState } from "react";
import api, { fileUrl } from "../../../lib/api";
import { AdminButton, AdminInput, AdminTextarea, AdminCard } from "../AdminUI";
import { Plus, Trash2, Loader2, Pencil, Check, X, ChevronUp, ChevronDown, Upload, ImageOff } from "lucide-react";
import { toast } from "sonner";

const emptyItem = { name: "", description: "", price: "", category_id: "", image_url: "", sort_order: 0 };

const MenuTab = () => {
    const [cats, setCats] = useState([]);
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [newCat, setNewCat] = useState({ name: "", description: "", sort_order: 0 });
    const [editingCat, setEditingCat] = useState(null);
    const [editingItem, setEditingItem] = useState(null);
    const [newItemFor, setNewItemFor] = useState(null);
    const [newItem, setNewItem] = useState(emptyItem);
    const [uploadingFor, setUploadingFor] = useState(null); // item id OR 'new'
    const newItemFileRef = useRef(null);

    const reload = async () => {
        const [c, i] = await Promise.all([
            api.get("/menu/categories"),
            api.get("/menu/items"),
        ]);
        setCats(c.data);
        setItems(i.data);
        setLoading(false);
    };

    useEffect(() => { reload(); }, []);

    // ---- Category ops ----
    const addCat = async () => {
        if (!newCat.name.trim()) return toast.error("Name ist erforderlich");
        try {
            await api.post("/menu/categories", { ...newCat, sort_order: cats.length });
            setNewCat({ name: "", description: "", sort_order: 0 });
            await reload();
            toast.success("Kategorie erstellt");
        } catch { toast.error("Fehler"); }
    };

    const saveCat = async (cat) => {
        await api.put(`/menu/categories/${cat.id}`, cat);
        setEditingCat(null);
        await reload();
        toast.success("Kategorie aktualisiert");
    };

    const removeCat = async (id) => {
        if (!window.confirm("Kategorie und alle Artikel löschen?")) return;
        await api.delete(`/menu/categories/${id}`);
        await reload();
        toast.success("Kategorie gelöscht");
    };

    const moveCat = async (id, dir) => {
        const ordered = [...cats];
        const idx = ordered.findIndex((c) => c.id === id);
        const target = idx + dir;
        if (target < 0 || target >= ordered.length) return;
        [ordered[idx], ordered[target]] = [ordered[target], ordered[idx]];
        setCats(ordered);
        try {
            await api.post("/menu/categories/reorder", { ids: ordered.map((c) => c.id) });
        } catch { toast.error("Fehler beim Sortieren"); reload(); }
    };

    // ---- Item ops ----
    const startNewItem = (catId) => {
        setNewItemFor(catId);
        setNewItem({ ...emptyItem, category_id: catId });
    };

    const uploadImage = async (file) => {
        const fd = new FormData();
        fd.append("file", file);
        const up = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        return up.data.url;
    };

    const onNewItemFile = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploadingFor("new");
        try {
            const url = await uploadImage(file);
            setNewItem({ ...newItem, image_url: url });
            toast.success("Bild hochgeladen");
        } catch { toast.error("Upload fehlgeschlagen"); }
        finally { setUploadingFor(null); if (newItemFileRef.current) newItemFileRef.current.value = ""; }
    };

    const onEditItemFile = async (e, item) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploadingFor(item.id);
        try {
            const url = await uploadImage(file);
            setEditingItem({ ...item, image_url: url });
            toast.success("Bild hochgeladen");
        } catch { toast.error("Upload fehlgeschlagen"); }
        finally { setUploadingFor(null); }
    };

    const addItem = async () => {
        if (!newItem.name.trim() || !newItem.price.trim()) return toast.error("Name und Preis sind erforderlich");
        const catItems = items.filter((i) => i.category_id === newItem.category_id);
        await api.post("/menu/items", { ...newItem, sort_order: catItems.length });
        setNewItemFor(null);
        setNewItem(emptyItem);
        await reload();
        toast.success("Artikel erstellt");
    };

    const saveItem = async (item) => {
        await api.put(`/menu/items/${item.id}`, item);
        setEditingItem(null);
        await reload();
        toast.success("Artikel aktualisiert");
    };

    const removeItem = async (id) => {
        if (!window.confirm("Artikel löschen?")) return;
        await api.delete(`/menu/items/${id}`);
        await reload();
        toast.success("Artikel gelöscht");
    };

    const moveItem = async (catId, id, dir) => {
        const ordered = items.filter((i) => i.category_id === catId);
        const others = items.filter((i) => i.category_id !== catId);
        const idx = ordered.findIndex((i) => i.id === id);
        const target = idx + dir;
        if (target < 0 || target >= ordered.length) return;
        [ordered[idx], ordered[target]] = [ordered[target], ordered[idx]];
        setItems([...others, ...ordered]);
        try {
            await api.post("/menu/items/reorder", { ids: ordered.map((i) => i.id) });
        } catch { toast.error("Fehler beim Sortieren"); reload(); }
    };

    if (loading) return <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-garden-green" /></div>;

    const renderItemImage = (url, size = "w-16 h-16") => url ? (
        <img src={fileUrl(url)} alt="" className={`${size} rounded-xl object-cover flex-shrink-0 border border-cream-dark`} />
    ) : (
        <div className={`${size} rounded-xl bg-cream-dark/50 grid place-items-center flex-shrink-0 border border-dashed border-cream-dark`}>
            <ImageOff className="w-5 h-5 text-deep-green/30" />
        </div>
    );

    return (
        <div className="space-y-6 max-w-4xl" data-testid="menu-tab">
            <AdminCard className="space-y-3">
                <h3 className="font-serif text-xl text-deep-green">Neue Kategorie</h3>
                <div className="grid sm:grid-cols-2 gap-3">
                    <AdminInput placeholder="Name (z.B. Softeis)" value={newCat.name} onChange={(e) => setNewCat({ ...newCat, name: e.target.value })} data-testid="new-cat-name" />
                    <AdminInput placeholder="Beschreibung (optional)" value={newCat.description} onChange={(e) => setNewCat({ ...newCat, description: e.target.value })} />
                </div>
                <AdminButton onClick={addCat} data-testid="new-cat-add">
                    <Plus className="w-4 h-4" /> Kategorie hinzufügen
                </AdminButton>
            </AdminCard>

            {cats.length === 0 && (
                <p className="text-deep-green/60 text-sm text-center py-6">Noch keine Kategorien vorhanden.</p>
            )}

            {cats.map((cat, catIdx) => (
                <AdminCard key={cat.id} className="space-y-4" data-testid={`cat-${cat.id}`}>
                    {editingCat?.id === cat.id ? (
                        <div className="space-y-2">
                            <div className="grid sm:grid-cols-2 gap-3">
                                <AdminInput value={editingCat.name} onChange={(e) => setEditingCat({ ...editingCat, name: e.target.value })} />
                                <AdminInput value={editingCat.description} onChange={(e) => setEditingCat({ ...editingCat, description: e.target.value })} />
                            </div>
                            <div className="flex gap-2">
                                <AdminButton onClick={() => saveCat(editingCat)}><Check className="w-4 h-4" /> Speichern</AdminButton>
                                <AdminButton variant="ghost" onClick={() => setEditingCat(null)}><X className="w-4 h-4" /> Abbrechen</AdminButton>
                            </div>
                        </div>
                    ) : (
                        <div className="flex items-center justify-between gap-3">
                            <div className="flex items-center gap-2">
                                <div className="flex flex-col">
                                    <button
                                        onClick={() => moveCat(cat.id, -1)}
                                        disabled={catIdx === 0}
                                        className="p-1 rounded hover:bg-cream-dark disabled:opacity-30"
                                        data-testid={`cat-up-${cat.id}`}
                                        title="Nach oben"
                                    ><ChevronUp className="w-4 h-4" /></button>
                                    <button
                                        onClick={() => moveCat(cat.id, 1)}
                                        disabled={catIdx === cats.length - 1}
                                        className="p-1 rounded hover:bg-cream-dark disabled:opacity-30"
                                        data-testid={`cat-down-${cat.id}`}
                                        title="Nach unten"
                                    ><ChevronDown className="w-4 h-4" /></button>
                                </div>
                                <div>
                                    <h4 className="font-serif text-lg text-deep-green">{cat.name}</h4>
                                    {cat.description && <p className="text-sm text-deep-green/60">{cat.description}</p>}
                                </div>
                            </div>
                            <div className="flex gap-2">
                                <AdminButton variant="ghost" onClick={() => setEditingCat(cat)} data-testid={`cat-edit-${cat.id}`}><Pencil className="w-4 h-4" /></AdminButton>
                                <AdminButton variant="danger" onClick={() => removeCat(cat.id)} data-testid={`cat-delete-${cat.id}`}><Trash2 className="w-4 h-4" /></AdminButton>
                            </div>
                        </div>
                    )}

                    <div className="border-t border-cream-dark pt-4 space-y-2">
                        {(() => {
                            const catItems = items.filter((i) => i.category_id === cat.id);
                            return catItems.map((item, itemIdx) => (
                                <div key={item.id} className="bg-cream-dark/40 rounded-xl p-3" data-testid={`item-${item.id}`}>
                                    {editingItem?.id === item.id ? (
                                        <div className="space-y-2">
                                            <div className="flex gap-3 items-start">
                                                {renderItemImage(editingItem.image_url, "w-20 h-20")}
                                                <div className="flex-1 space-y-2">
                                                    <div className="grid sm:grid-cols-2 gap-2">
                                                        <AdminInput placeholder="Name" value={editingItem.name} onChange={(e) => setEditingItem({ ...editingItem, name: e.target.value })} />
                                                        <AdminInput placeholder="Preis" value={editingItem.price} onChange={(e) => setEditingItem({ ...editingItem, price: e.target.value })} />
                                                    </div>
                                                    <AdminTextarea rows={2} placeholder="Beschreibung" value={editingItem.description} onChange={(e) => setEditingItem({ ...editingItem, description: e.target.value })} />
                                                </div>
                                            </div>
                                            <div className="flex gap-2 flex-wrap">
                                                <label className="cursor-pointer">
                                                    <input type="file" accept="image/png,image/jpeg,image/webp,image/gif" className="hidden" onChange={(e) => onEditItemFile(e, editingItem)} />
                                                    <span className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium bg-cream-dark hover:bg-cream-dark/80 text-deep-green">
                                                        {uploadingFor === editingItem.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                                                        {editingItem.image_url ? "Bild ändern" : "Bild hinzufügen"}
                                                    </span>
                                                </label>
                                                {editingItem.image_url && (
                                                    <AdminButton variant="ghost" onClick={() => setEditingItem({ ...editingItem, image_url: "" })}>
                                                        <ImageOff className="w-4 h-4" /> Bild entfernen
                                                    </AdminButton>
                                                )}
                                                <AdminButton onClick={() => saveItem(editingItem)}><Check className="w-4 h-4" /> Speichern</AdminButton>
                                                <AdminButton variant="ghost" onClick={() => setEditingItem(null)}><X className="w-4 h-4" /> Abbrechen</AdminButton>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="flex items-center gap-3">
                                            <div className="flex flex-col">
                                                <button
                                                    onClick={() => moveItem(cat.id, item.id, -1)}
                                                    disabled={itemIdx === 0}
                                                    className="p-0.5 rounded hover:bg-white disabled:opacity-30"
                                                    data-testid={`item-up-${item.id}`}
                                                ><ChevronUp className="w-4 h-4" /></button>
                                                <button
                                                    onClick={() => moveItem(cat.id, item.id, 1)}
                                                    disabled={itemIdx === catItems.length - 1}
                                                    className="p-0.5 rounded hover:bg-white disabled:opacity-30"
                                                    data-testid={`item-down-${item.id}`}
                                                ><ChevronDown className="w-4 h-4" /></button>
                                            </div>
                                            {renderItemImage(item.image_url)}
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-baseline gap-3 flex-wrap">
                                                    <span className="font-medium text-deep-green">{item.name}</span>
                                                    <span className="font-cursive text-xl text-garden-green-dark">{item.price}</span>
                                                </div>
                                                {item.description && <div className="text-sm text-deep-green/60">{item.description}</div>}
                                            </div>
                                            <div className="flex gap-2 flex-shrink-0">
                                                <AdminButton variant="ghost" onClick={() => setEditingItem(item)} data-testid={`item-edit-${item.id}`}><Pencil className="w-4 h-4" /></AdminButton>
                                                <AdminButton variant="danger" onClick={() => removeItem(item.id)} data-testid={`item-delete-${item.id}`}><Trash2 className="w-4 h-4" /></AdminButton>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ));
                        })()}

                        {newItemFor === cat.id ? (
                            <div className="bg-leaf-green/10 rounded-xl p-3 space-y-2">
                                <div className="flex gap-3 items-start">
                                    {renderItemImage(newItem.image_url, "w-20 h-20")}
                                    <div className="flex-1 space-y-2">
                                        <div className="grid sm:grid-cols-2 gap-2">
                                            <AdminInput placeholder="Name" value={newItem.name} onChange={(e) => setNewItem({ ...newItem, name: e.target.value })} data-testid="new-item-name" />
                                            <AdminInput placeholder="Preis (z.B. 2,50 €)" value={newItem.price} onChange={(e) => setNewItem({ ...newItem, price: e.target.value })} data-testid="new-item-price" />
                                        </div>
                                        <AdminTextarea rows={2} placeholder="Beschreibung (optional)" value={newItem.description} onChange={(e) => setNewItem({ ...newItem, description: e.target.value })} />
                                    </div>
                                </div>
                                <div className="flex gap-2 flex-wrap">
                                    <label className="cursor-pointer">
                                        <input ref={newItemFileRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif" className="hidden" onChange={onNewItemFile} />
                                        <span className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium bg-cream-dark hover:bg-cream-dark/80 text-deep-green">
                                            {uploadingFor === "new" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                                            {newItem.image_url ? "Bild ändern" : "Bild hinzufügen"}
                                        </span>
                                    </label>
                                    {newItem.image_url && (
                                        <AdminButton variant="ghost" onClick={() => setNewItem({ ...newItem, image_url: "" })}>
                                            <ImageOff className="w-4 h-4" /> Bild entfernen
                                        </AdminButton>
                                    )}
                                    <AdminButton onClick={addItem} data-testid="new-item-add"><Plus className="w-4 h-4" /> Hinzufügen</AdminButton>
                                    <AdminButton variant="ghost" onClick={() => { setNewItemFor(null); setNewItem(emptyItem); }}><X className="w-4 h-4" /> Abbrechen</AdminButton>
                                </div>
                            </div>
                        ) : (
                            <AdminButton variant="secondary" onClick={() => startNewItem(cat.id)} data-testid={`add-item-to-${cat.id}`}>
                                <Plus className="w-4 h-4" /> Artikel hinzufügen
                            </AdminButton>
                        )}
                    </div>
                </AdminCard>
            ))}
        </div>
    );
};

export default MenuTab;
